import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  CalendarDays,
  Building2,
  Users,
  QrCode,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Smartphone,
  Radio,
} from "lucide-react";
import { compareEventsByNearestStart, compareEventsForListing } from "@mvk/shared";
import type { Event } from "../../types";
import { tryAdminEventsList } from "../../services/adminEventsService";
import { apiRequest } from "../../api/client";
import { AdminEventCover, eventCoverSrc } from "../components/AdminEventCover";

const statusConfig = {
  active: { label: "Идёт сейчас", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  upcoming: { label: "Скоро", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  past: { label: "Завершено", color: "bg-gray-100 text-gray-500", dot: "bg-gray-400" },
};

type AdminOverview = {
  totalUsers: number;
  enteredApp: number;
  onlineNow: number;
  onlineWindowMinutes: number;
  totalScans: number;
  uniqueScanners: number;
  uniqueVisitors: number;
  uniqueExhibitors: number;
};

function formatDateRange(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  const fmt = (d: Date) =>
    d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  return `${fmt(s)} — ${fmt(e)} ${e.getFullYear()}`;
}

function StatCard({
  label,
  value,
  sub,
  icon,
  bg,
  accent,
}: {
  label: string;
  value: string | number;
  sub: string;
  icon: React.ReactNode;
  bg: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${
        accent
          ? "bg-sky-50/80 border-sky-100"
          : "bg-white border-gray-100"
      }`}
    >
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>{icon}</div>
      <div>
        <div className="text-[24px] text-gray-900 leading-none tabular-nums">{value}</div>
        <div className="text-[12px] text-gray-500 mt-1">{label}</div>
        <div className="text-[11px] text-gray-400 mt-0.5 leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function EventListRow({
  event,
  onOpen,
}: {
  event: Event;
  onOpen: (id: string) => void;
}) {
  const cfg = statusConfig[event.status];
  return (
    <button
      type="button"
      onClick={() => onOpen(event.id)}
      className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-left"
    >
      <div className="w-[72px] flex-shrink-0">
        <AdminEventCover
          src={eventCoverSrc(event)}
          alt={event.title}
          rounded="rounded-lg"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[13px] text-gray-900 truncate">{event.title}</span>
          <span
            className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${cfg.color}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        </div>
        <div className="text-[12px] text-gray-400">
          {event.city} · {formatDateRange(event.startDate, event.endDate)}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[13px] text-gray-900">{event.companiesCount}</div>
        <div className="text-[11px] text-gray-400">экспонентов</div>
      </div>
      <ArrowRight size={14} className="text-gray-300 flex-shrink-0" />
    </button>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    const data = await apiRequest<AdminOverview>("/me/admin-overview", {
      audience: "admin",
    }).catch(() => null);
    if (data) setOverview(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [list] = await Promise.all([
        tryAdminEventsList().catch(() => null),
        loadOverview(),
      ]);
      if (!cancelled) {
        setEvents(list ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOverview]);

  // Live online counter — presence heartbeat window is ~15 min; refresh often enough.
  useEffect(() => {
    const id = window.setInterval(() => {
      void loadOverview();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [loadOverview]);

  const activeEvents = events.filter((e) => e.status === "active").length;
  const upcomingEvents = events.filter((e) => e.status === "upcoming").length;
  const uniqueVisitors = overview?.uniqueVisitors ?? 0;
  const uniqueExhibitors = overview?.uniqueExhibitors ?? 0;

  const { currentEvents, pastEvents } = useMemo(() => {
    const current = events
      .filter((e) => e.status !== "past")
      .sort(compareEventsByNearestStart)
      .slice(0, 10);
    const past = events
      .filter((e) => e.status === "past")
      .sort(compareEventsForListing)
      .slice(0, 6);
    return { currentEvents: current, pastEvents: past };
  }, [events]);

  const totalScans = overview?.totalScans ?? 0;
  const onlineWindow = overview?.onlineWindowMinutes ?? 15;

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-[22px] text-gray-900">Обзор</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">
          Добро пожаловать в панель управления MVK One
        </p>
      </div>

      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[14px] text-gray-900">Приложение</h2>
          <p className="text-[11px] text-gray-400">
            Глобально · онлайн = активность за {onlineWindow} мин
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            label="Пользователей"
            value={(overview?.totalUsers ?? 0).toLocaleString("ru-RU")}
            sub="Учётки в mvk.users по всей платформе"
            icon={<Users size={20} className="text-indigo-500" />}
            bg="bg-indigo-50"
          />
          <StatCard
            label="Заходили в приложение"
            value={(overview?.enteredApp ?? 0).toLocaleString("ru-RU")}
            sub={
              overview
                ? `из ${(overview.totalUsers ?? 0).toLocaleString("ru-RU")} пользователей`
                : "хотя бы раз открыли мини-апп / PWA"
            }
            icon={<Smartphone size={20} className="text-violet-500" />}
            bg="bg-violet-50"
          />
          <StatCard
            label="Сейчас онлайн"
            value={(overview?.onlineNow ?? 0).toLocaleString("ru-RU")}
            sub={`Heartbeat за последние ${onlineWindow} мин · все выставки`}
            icon={<Radio size={20} className="text-sky-500" />}
            bg="bg-sky-100"
            accent
          />
          <StatCard
            label="QR-сканирований"
            value={totalScans.toLocaleString("ru-RU")}
            sub={
              totalScans === 0
                ? "Пока нет сканов в системе"
                : overview?.uniqueScanners
                  ? `${overview.uniqueScanners.toLocaleString("ru-RU")} уникальных сканеров`
                  : "Все события"
            }
            icon={<QrCode size={20} className="text-emerald-500" />}
            bg="bg-emerald-50"
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[14px] text-gray-900">Мероприятия</h2>
          <p className="text-[11px] text-gray-400">Сводка по каталогу событий</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <StatCard
            label="Мероприятий всего"
            value={events.length}
            sub={`${activeEvents} активных · ${upcomingEvents} предстоящих`}
            icon={<CalendarDays size={20} className="text-indigo-500" />}
            bg="bg-indigo-50"
          />
          <StatCard
            label="Участников"
            value={uniqueExhibitors.toLocaleString("ru-RU")}
            sub="Уникальные люди с доступом участника"
            icon={<Building2 size={20} className="text-violet-500" />}
            bg="bg-violet-50"
          />
          <StatCard
            label="Посетителей"
            value={uniqueVisitors.toLocaleString("ru-RU")}
            sub="Уникальные люди с билетом · без пересечений"
            icon={<Users size={20} className="text-sky-500" />}
            bg="bg-sky-50"
          />
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-50">
            <h2 className="text-[14px] text-gray-900">Мероприятия</h2>
            <button
              type="button"
              onClick={() => navigate("/events")}
              className="flex items-center gap-1 text-[12px] text-indigo-500 hover:text-indigo-700"
            >
              Все <ArrowRight size={13} />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {currentEvents.length === 0 && pastEvents.length === 0 ? (
              <p className="px-5 py-6 text-[13px] text-gray-400">Пока нет мероприятий</p>
            ) : null}
            {currentEvents.map((event) => (
              <EventListRow
                key={event.id}
                event={event}
                onOpen={(id) => navigate(`/events/${id}`)}
              />
            ))}
            {pastEvents.length > 0 ? (
              <>
                <div className="px-5 py-2.5 bg-gray-50/80 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-[11px] uppercase tracking-wide text-gray-400 flex-shrink-0">
                    Прошедшие
                  </span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                {pastEvents.map((event) => (
                  <EventListRow
                    key={event.id}
                    event={event}
                    onOpen={(id) => navigate(`/events/${id}`)}
                  />
                ))}
              </>
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <h2 className="text-[14px] text-gray-900">Быстрые действия</h2>
          <div className="space-y-2">
            {[
              { label: "Создать мероприятие", href: "/events", icon: <CalendarDays size={16} /> },
              { label: "Управление экспонентами", href: "/events", icon: <Building2 size={16} /> },
              { label: "Аналитика", href: "/events", icon: <TrendingUp size={16} /> },
            ].map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => navigate(action.href)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500">
                  {action.icon}
                </div>
                <span className="text-[13px] text-gray-700">{action.label}</span>
                <ArrowRight size={14} className="text-gray-300 ml-auto" />
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-gray-50 space-y-2">
            <div className="flex items-center gap-2 text-[12px] text-emerald-600">
              <CheckCircle2 size={14} />
              Система работает нормально
            </div>
            {overview ? (
              <p className="text-[11px] text-gray-400 leading-snug">
                Сейчас онлайн:{" "}
                <span className="text-sky-600 tabular-nums">
                  {overview.onlineNow.toLocaleString("ru-RU")}
                </span>
                {" · "}
                обновляется каждые 30 сек
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
